import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { CommanServices } from '@/services/commanServices';
import { Boxes, Plus, Pencil, Trash2, User, Users, Shield, Repeat, Clock, Hash, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import ResourceFormModal from './ResourceFormModal';
import {
  AVAILABILITY, UNITS, num, money, unitLabel, blankResource,
  resourceToTimelinePayload, buildResourceFullItem,
} from './resourceInject';

const AVAILABILITY_ICONS = { once: Hash, reuse: Repeat, time: Clock };
const userName = (u) => u?.full_name || u?.fullName || u?.name || u?.userName || u?.username || u?.email || 'User';

const EntityIcon = ({ type, className }) => {
  if (type === 'card') return <Boxes className={className} />;
  if (type === 'task') return <Hash className={className} />;
  if (type === 'team') return <Users className={className} />;
  if (type === 'crew') return <Shield className={className} />;
  return <User className={className} />;
};

export default function IncidentResourcesPanel({ incident, isAdmin = false }) {
  const incidentId = incident?.id;
  const [resources, setResources] = useState([]);
  const [draft, setDraft] = useState(null);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConsumption, setShowConsumption] = useState(false);

  const [linkedByResource, setLinkedByResource] = useState({}); // resourceId -> units linked on cards/tasks

  const loadResources = useCallback(async () => {
    if (!incidentId) return;
    try {
      const res = await CommanServices.getIncidentResourceUsage(incidentId);
      const list = res?.status && Array.isArray(res.data) ? res.data : [];
      setResources(list);
      const linked = Object.fromEntries(list.map((resource) => [resource.id, num(resource.linkedQuantity)]));
      setLinkedByResource(linked);
    } catch (e) {
      setResources([]);
    } finally {
      setLoading(false);
    }
  }, [incidentId]);

  useEffect(() => { loadResources(); }, [loadResources]);

  // Refresh when the timeline changes elsewhere (e.g. resource added from the timeline group)
  useEffect(() => {
    const handler = () => loadResources();
    window.addEventListener('timelineRefresh', handler);
    return () => window.removeEventListener('timelineRefresh', handler);
  }, [loadResources]);

  useEffect(() => {
    if (!incidentId) return;
    (async () => {
      try {
        const res = await CommanServices.getincidentTimeline(incidentId, new URLSearchParams());
        const items = res?.status ? (await res.data) || [] : [];
        const opts = [];
        items.forEach((it) => {
          if (!it || (it.group !== 'response_action' && it.group !== 'pre_incident_action') || it.is_active === false) return;
          const cardName = it.meta?.actionCardName || it.title || 'Action Card';
          opts.push({ type: 'card', id: String(it.id), name: cardName });
          (it.meta?.tasks || []).forEach((t, idx) => {
            const tid = t.id || `${it.id}::task::${idx}`;
            opts.push({ type: 'task', id: String(tid), name: `${cardName} › ${t.title || `Task ${idx + 1}`}`, parentId: String(it.id) });
          });
        });
        setEntities(opts);
      } catch (e) { /* noop */ }
    })();
  }, [incidentId]);

  // Cost of one resource row: stock value for single-use/time-based. Reusable
  // allocations are finite too, while actual use cost accrues per recorded use.
  const rowCost = useCallback(
    (r) => (r.availability === 'reuse'
      ? (linkedByResource[r.id] || 0) * num(r.cost)
      : num(r.quantity) * num(r.cost)),
    [linkedByResource]
  );
  const grandTotal = useMemo(
    () => resources.reduce((sum, r) => sum + rowCost(r), 0),
    [resources, rowCost]
  );

  const usageList = useMemo(
    () => resources.filter((r) => num(r.usageSummary?.totalUsageQuantity) > 0),
    [resources]
  );
  const totalUsageUnits = useMemo(
    () => resources.reduce((s, r) => s + num(r.usageSummary?.totalUsageQuantity), 0),
    [resources]
  );

  const save = async (form) => {
    if (!form.category?.trim()) { toast.error('Resource type (category) is required'); return; }
    try {
      if (form.id) {
        const res = await CommanServices.getincidentTimeline(incidentId, new URLSearchParams());
        const items = res?.status ? (await res.data) || [] : [];
        const existing = items.find((it) => it.id === form.id) || { id: form.id };
        await CommanServices.updatePutIncidentTimeline(incidentId, form.id, buildResourceFullItem(existing, form));
      } else {
        await CommanServices.addincidentTimeline(incidentId, resourceToTimelinePayload(form));
      }
      setDraft(null);
      toast.success(form.id ? 'Resource updated' : 'Resource added');
      await loadResources();
      window.dispatchEvent(new Event('timelineRefresh'));
    } catch (e) {
      toast.error('Failed to save resource');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this resource?')) return;
    try {
      await CommanServices.deleteTimelineItem(incidentId, id);
      await loadResources();
      window.dispatchEvent(new Event('timelineRefresh'));
      toast.success('Resource removed');
    } catch (e) {
      toast.error('Failed to remove resource');
    }
  };

  return (
    <div className="bg-white overflow-hidden" data-testid="incident-resources-panel">
      <div className="flex items-center justify-between px-5 py-3.5 border-b bg-slate-50/60">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white">
            <Boxes className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-sm font-bold text-gray-900 leading-tight">Resources</h3>
            <p className="text-[11px] text-gray-500">Added as timeline injects · allocated per action card / task</p>
          </div>
        </div>
        {isAdmin && (
          <button
            data-testid="add-resource-btn"
            onClick={() => setDraft(blankResource())}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold px-3 py-2 hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" /> Add Resource
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-10 text-sm text-gray-400" data-testid="resources-loading">Loading resources…</div>
      ) : resources.length === 0 ? (
        <div className="text-center py-10" data-testid="resources-empty">
          <Boxes className="h-9 w-9 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500 font-medium">No resources added yet</p>
          {isAdmin && <p className="text-xs text-gray-400 mt-1">{incident?.is_exercise ? 'Add resources that trainees can use during the drill.' : 'Add resources that responders can use during this incident.'}</p>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="resources-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-400 bg-gray-50/70 border-b">
                <th className="text-left font-bold px-4 py-2.5">Type</th>
                <th className="text-center font-bold px-3 py-2.5">Qty</th>
                <th className="text-left font-bold px-3 py-2.5">Availability</th>
                <th className="text-right font-bold px-3 py-2.5">Unit Cost</th>
                <th className="text-right font-bold px-3 py-2.5">Total Cost</th>
                <th className="text-left font-bold px-3 py-2.5">Linked / Available</th>
                {isAdmin && <th className="px-3 py-2.5"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {resources.map((r) => {
                const av = AVAILABILITY[r.availability] || AVAILABILITY.once;
                const AvIcon = AVAILABILITY_ICONS[r.availability] || Hash;
                return (
                  <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`resource-row-${r.id}`}>
                    <td className="px-4 py-3 font-semibold text-gray-900">{r.category}</td>
                    <td className="px-3 py-3 text-center font-bold text-gray-700">{num(r.quantity)}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${av.color}`}>
                        <AvIcon className="h-3 w-3" /> {av.label}
                        {r.availability === 'time' && r.replenishEvery
                          ? <span className="font-normal opacity-80">· every {r.replenishEvery} {unitLabel(r.replenishUnit)}</span>
                          : null}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right text-gray-600">{money(r.cost)}</td>
                    <td className="px-3 py-3 text-right font-bold text-gray-900" title={r.availability === 'reuse' ? 'Allocated reusable units × unit cost' : 'Stock value: quantity × unit cost'}>
                      {money(rowCost(r))}
                      {r.availability === 'reuse' && <span className="ml-1 text-[9px] font-semibold text-teal-600 align-middle">per use</span>}
                    </td>
                    <td className="px-3 py-3" data-testid={`resource-linkage-${r.id}`}>
                      {(() => {
                        const linked = linkedByResource[r.id] || 0;
                        const avail = Math.max(0, num(r.quantity) - linked);
                        return linked > 0 ? (
                          <span className="text-[12px] text-gray-700">
                            <span className="font-bold">{linked}</span> linked ·{' '}
                            <span className={`font-bold ${avail === 0 ? 'text-red-600' : 'text-emerald-700'}`}>{avail}</span> available
                          </span>
                        ) : (
                          <span className="text-[12px] text-gray-300">Not linked — use “Link Resource” on an action card</span>
                        );
                      })()}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button data-testid={`edit-resource-${r.id}`} onClick={() => setDraft({ ...r })} className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-indigo-600"><Pencil className="h-3.5 w-3.5" /></button>
                          <button data-testid={`delete-resource-${r.id}`} onClick={() => remove(r.id)} className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-slate-50/70">
                <td className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500" colSpan={4}>Grand Total</td>
                <td className="px-3 py-3 text-right text-base font-black text-indigo-700" data-testid="resources-grand-total">{money(grandTotal)}</td>
                <td colSpan={isAdmin ? 2 : 1}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Resource Consumption — collapsible */}
      {!loading && resources.length > 0 && (
        <div className="border-t" data-testid="resource-consumption-section">
          <button
            type="button"
            onClick={() => setShowConsumption((v) => !v)}
            data-testid="resource-consumption-toggle"
            className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50/60 transition-colors"
          >
            <span className="flex items-center gap-2">
              <span className="text-[11px] font-black uppercase tracking-widest text-gray-600">Resource Usage &amp; Consumption</span>
              {totalUsageUnits > 0 && (
                <span className="text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5" data-testid="resource-consumption-count">
                  {totalUsageUnits} used
                </span>
              )}
            </span>
            <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showConsumption ? 'rotate-180' : ''}`} />
          </button>
          {showConsumption && (
            <div className="px-5 pb-4 space-y-2" data-testid="resource-consumption-body">
              {usageList.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">
                  No resource usage recorded yet. Responders record usage with “Use”; completing work does not automatically consume linked resources.
                </p>
              ) : (
                usageList.map((r) => {
                  const total = num(r.quantity);
                  const summary = r.usageSummary || {};
                  const consumed = num(summary.consumed);
                  const remaining = num(summary.availableQuantity);
                  const pct = total > 0 ? Math.min(100, Math.round((consumed / total) * 100)) : 0;
                  const activeReusable = num(summary.activeReusableQuantity);
                  const activeTimed = num(summary.activeTimedQuantity);
                  const usageLabel = r.availability === 'reuse'
                    ? `${num(summary.reusableUses)} total uses · ${activeReusable} active · ${remaining} available`
                    : r.availability === 'time'
                      ? `${num(summary.timedUses)} total uses · ${activeTimed} active · ${remaining} available`
                      : `Consumed ${consumed} of ${total} · ${remaining} remaining`;
                  return (
                    <div key={r.id} className="rounded-lg border border-gray-100 bg-slate-50/60 px-3 py-2" data-testid={`resource-consumption-row-${r.id}`}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-gray-800">{r.category}</span>
                        <span className="text-gray-500">
                          {usageLabel} · <span className="font-bold text-indigo-700">SAR {money(summary.accruedCost)} accrued</span>
                        </span>
                      </div>
                      {r.availability === 'once' && (
                        <div className="mt-1.5 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                          <div className="h-full rounded-full bg-rose-500 transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {draft && (
        <ResourceFormModal draft={draft} entities={entities} incident={incident} onSave={save} onClose={() => setDraft(null)} />
      )}
    </div>
  );
}
